/**
 * ProviderOnboardingPanel.jsx — the admin's way into provider onboarding preparation (MASTER_FLOW §5).
 *
 * The gap this closes is recorded in `docs/FULL_PRODUCT_ROADMAP.md` §1.1 row 12 as *"Page is read-only
 * transparency; no GUI to add/onboard a provider"*, and it closes the PREPARATION half of it only. The
 * panel prepares and validates a draft: it sends the seven draft fields to
 * `POST /api/connections/provider-onboarding/preview` and renders the read-only projection that comes
 * back. There is no install, no connect, no enable, no "activate" and no credential field for a secret
 * VALUE, because provisioning and activation stay operator-gated and the server refuses a value.
 *
 * Three rules make that structural rather than a promise:
 *
 *   1. **Every closed set comes from the server.** The draft fields, the template the form starts from,
 *      the data classes, the capability tokens, the verdicts, the severities, the reasons and the
 *      boundaries are all read from `GET /api/connections/provider-onboarding/contract` and rendered
 *      from that answer. This file holds no option list of its own — `providerOnboardingVocabulary`
 *      supplies the Hebrew for each token and nothing else. A hardcoded list here would keep offering
 *      an arm the preview has renamed, and on THIS surface the stale arm would be a field name or a
 *      refusal reason.
 *
 *   2. **The form starts from the server's own template, placeholders included.** `contract.template`
 *      is the draft the preview itself builds, so an operator who submits it untouched sees exactly the
 *      findings the server will raise about it (`field_placeholder_unresolved` on the four templated
 *      fields, `field_absent` on the two empty lists). Reconstructing the placeholder grammar here
 *      would have meant a client-side guess about what the server will accept.
 *
 *   3. **The activation block is printed on EVERY answer, in every verdict.** "Nothing was provisioned
 *      and nothing was enabled" is true whether or not the draft is ready, and a card that only denied
 *      authority after a successful preview would be silent exactly where an operator is most likely to
 *      assume one. It is rendered from the server's own block — a key that is not exactly `false` is
 *      reported as unknown rather than dropped.
 *
 * The one control that is NOT built from an option list is `credential_secret_ref`, and that is
 * deliberate: it takes a REFERENCE (`env:NAME`, `kv:path/name`) and the preview refuses a value. The
 * field says so, and a refusal comes back as a finding that names the field and carries no part of
 * what was typed.
 */

import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { useAuth } from "../context/AuthContext";
import { connectionsApi } from "../services/api";
import {
  PROVIDER_ONBOARDING_CAVEATS_NOTE_HE,
  PROVIDER_ONBOARDING_CREDENTIAL_REF_NOTE_HE,
  PROVIDER_ONBOARDING_UNKNOWN_HE,
  providerOnboardingActivationSentences,
  providerOnboardingActivationStateLabel,
  providerOnboardingBoundaryLabels,
  providerOnboardingCapabilityLabel,
  providerOnboardingCredentialStateLabel,
  providerOnboardingDataClassLabel,
  providerOnboardingFalseFlag,
  providerOnboardingFieldLabel,
  providerOnboardingFindingLabels,
  providerOnboardingRegistryStateLabel,
  providerOnboardingVerdictLabel,
} from "./providerOnboardingVocabulary";

const VERDICT_TONE = {
  ready_for_operator_provisioning: "bg-green-50 text-green-700 border-green-200",
  incomplete: "bg-amber-50 text-amber-800 border-amber-300",
  refused: "bg-red-50 text-red-600 border-red-200",
};

const SEVERITY_TONE = {
  refusal: "bg-red-50 text-red-600 border-red-200",
  incomplete: "bg-amber-50 text-amber-800 border-amber-300",
  disclosure: "bg-gray-50 text-gray-500 border-gray-200",
};

function Chip({ children, tone }) {
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full border ${tone}`}>{children}</span>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex gap-2 text-xs">
      <span className="text-gray-400 shrink-0 min-w-36">{label}</span>
      <span className={`text-gray-700 break-all ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}

/**
 * Which control a draft field gets, decided from the CONTRACT and not from a list in this file.
 *
 * A field the contract publishes choices for is a closed vocabulary (a select, or a checkbox group when
 * it is also a list field); every other list field is one entry per line; everything else is text.
 */
function controlFor(field, contract) {
  const choices = contract?.choice_fields;
  if (choices && typeof choices === "object" && Object.prototype.hasOwnProperty.call(choices, field)) {
    return Array.isArray(contract?.list_fields) && contract.list_fields.includes(field)
      ? "choices-many"
      : "choices-one";
  }
  if (Array.isArray(contract?.list_fields) && contract.list_fields.includes(field)) return "lines";
  return "text";
}

/** A closed-vocabulary option's label: the Hebrew, or the raw token when the server names one this
 * module has no word for. Never "לא מזוהה" alone — the token is what the API calls it. */
function choiceLabel(label, token) {
  return label === PROVIDER_ONBOARDING_UNKNOWN_HE ? token : label;
}

/**
 * The form's starting state: the SERVER's template, not a client-side reconstruction of it.
 *
 * A list field starts as an empty string; `capabilities` is kept as an array because it is a checkbox
 * group rather than a textarea.
 */
function draftFromTemplate(contract) {
  const template = contract?.template && typeof contract.template === "object" ? contract.template : {};
  const fields = {};
  for (const field of Array.isArray(contract?.request_fields) ? contract.request_fields : []) {
    if (controlFor(field, contract) === "choices-many") continue;
    const value = template[field];
    fields[field] = Array.isArray(value) ? value.join("\n") : typeof value === "string" ? value : "";
  }
  return fields;
}

function capabilitiesFromTemplate(contract) {
  const value = contract?.template?.capabilities;
  return Array.isArray(value) ? value.filter((entry) => typeof entry === "string") : [];
}

/**
 * The request body. Blank text is sent as ABSENT, never as an empty claim — an operator who cleared a
 * required field is told `field_absent`, which is the truth, rather than having an empty string read as
 * a declaration about the provider.
 */
function payloadFrom(fields, capabilities, contract) {
  const payload = {};
  for (const [field, value] of Object.entries(fields)) {
    const control = controlFor(field, contract);
    if (control === "lines") {
      const entries = String(value ?? "")
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);
      if (entries.length) payload[field] = entries;
    } else if (String(value ?? "").trim()) {
      payload[field] = value;
    }
  }
  if (capabilities.length) payload.capabilities = capabilities;
  return payload;
}

/** One labelled text input, with the field's Hebrew label and the server's field name on screen. */
function Field({ field, value, onChange }) {
  return (
    <label className="block">
      <span className="block text-xs text-gray-500 mb-0.5">
        {providerOnboardingFieldLabel(field)} <span className="font-mono text-gray-400">({field})</span>
      </span>
      <input
        type="text"
        dir="ltr"
        value={value}
        onChange={(e) => onChange(field, e.target.value)}
        className="w-full border rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
        data-testid={`provider-onboarding-field-${field}`}
      />
    </label>
  );
}

/**
 * The draft form, built entirely from the contract's `request_fields`, `choice_fields`, `list_fields`
 * and `template`.
 */
export function ProviderOnboardingForm({ contract, fields, capabilities, onChange, onToggle }) {
  const requestFields = Array.isArray(contract?.request_fields) ? contract.request_fields : [];
  const choiceFields =
    contract?.choice_fields && typeof contract.choice_fields === "object" ? contract.choice_fields : {};

  return (
    <div className="space-y-4" data-testid="provider-onboarding-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {requestFields.map((field) => {
          const control = controlFor(field, contract);
          if (control === "choices-one") {
            const choices = Array.isArray(choiceFields[field]) ? choiceFields[field] : [];
            const current = String(fields[field] ?? "");
            // The option list must CONTAIN the control's value: the template's own placeholder is not a
            // data class, so a select built from `choice_fields` alone would render blank and hide the
            // one value the operator most needs to see is still the placeholder.
            const options = current && !choices.includes(current) ? [...choices, current] : [...choices];
            return (
              <label key={field} className="block">
                <span className="block text-xs text-gray-500 mb-0.5">
                  {providerOnboardingFieldLabel(field)}{" "}
                  <span className="font-mono text-gray-400">({field})</span>
                </span>
                <select
                  dir="ltr"
                  value={current}
                  onChange={(e) => onChange(field, e.target.value)}
                  className="w-full border rounded-lg px-2 py-1.5 text-sm bg-white"
                  data-testid={`provider-onboarding-field-${field}`}
                >
                  {options.map((token) => (
                    <option key={token} value={token}>
                      {choiceLabel(providerOnboardingDataClassLabel(token), token)}
                    </option>
                  ))}
                </select>
              </label>
            );
          }
          if (control === "lines") {
            return (
              <label key={field} className="block">
                <span className="block text-xs text-gray-500 mb-0.5">
                  {providerOnboardingFieldLabel(field)}{" "}
                  <span className="font-mono text-gray-400">({field})</span> — שורה לכל ערך
                </span>
                <textarea
                  dir="ltr"
                  rows={3}
                  value={fields[field] ?? ""}
                  onChange={(e) => onChange(field, e.target.value)}
                  className="w-full border rounded-lg px-2 py-1.5 text-sm font-mono"
                  data-testid={`provider-onboarding-field-${field}`}
                />
              </label>
            );
          }
          return (
            <Field
              key={field}
              field={field}
              value={fields[field] ?? ""}
              onChange={onChange}
            />
          );
        })}
      </div>

      {requestFields
        .filter((field) => controlFor(field, contract) === "choices-many")
        .map((field) => {
          const choices = Array.isArray(choiceFields[field]) ? choiceFields[field] : [];
          return (
            <fieldset key={field} className="border rounded-lg p-3" data-testid={`provider-onboarding-choices-${field}`}>
              <legend className="text-xs text-gray-500 px-1">
                {providerOnboardingFieldLabel(field)}{" "}
                <span className="font-mono text-gray-400">({field})</span> — רשות
              </legend>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                {choices.map((token) => (
                  <label key={token} className="flex items-center gap-2 text-xs text-gray-700">
                    <input
                      type="checkbox"
                      checked={capabilities.includes(token)}
                      onChange={() => onToggle(token)}
                      data-testid={`provider-onboarding-choice-${field}-${token}`}
                    />
                    {choiceLabel(providerOnboardingCapabilityLabel(token), token)}
                  </label>
                ))}
              </div>
            </fieldset>
          );
        })}

      <p className="text-xs text-gray-500" data-testid="provider-onboarding-credential-note">
        {PROVIDER_ONBOARDING_CREDENTIAL_REF_NOTE_HE}
      </p>
    </div>
  );
}

/**
 * The answer, rendered. Exported so it can be tested on a real server answer without a network call:
 * every acceptance-relevant claim on this surface is decided here.
 */
export function ProviderOnboardingAnswer({ answer, contract }) {
  if (!answer) return null;
  const findings = providerOnboardingFindingLabels(answer.findings);
  const draft = answer.draft && typeof answer.draft === "object" ? answer.draft : {};
  const registry = answer.registry && typeof answer.registry === "object" ? answer.registry : {};
  const credential = answer.credential && typeof answer.credential === "object" ? answer.credential : {};
  const fieldsToFix = Array.isArray(answer.fields_to_fix) ? answer.fields_to_fix : [];

  return (
    <div className="space-y-3" data-testid="provider-onboarding-answer">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-gray-800">תוצאת ההכנה</span>
        <span data-testid="provider-onboarding-verdict">
          <Chip tone={VERDICT_TONE[answer.verdict] || "bg-gray-50 text-gray-500 border-gray-200"}>
            {providerOnboardingVerdictLabel(answer.verdict)}
          </Chip>
        </span>
        <Chip tone="bg-gray-50 text-gray-500 border-gray-200">
          {providerOnboardingActivationStateLabel(contract?.activation_state)}
        </Chip>
      </div>

      {findings.length > 0 && (
        <div className="border rounded-lg p-3 space-y-1" data-testid="provider-onboarding-findings">
          <div className="text-xs font-semibold text-gray-500">מה שהבדיקה מצאה</div>
          {findings.map((finding, index) => (
            <div
              key={`${finding.severity}-${finding.field}-${finding.reason}-${index}`}
              className="flex flex-wrap items-center gap-2 text-xs"
              data-testid={`provider-onboarding-finding-${finding.field}-${finding.reason}`}
            >
              <Chip tone={SEVERITY_TONE[finding.severity] || "bg-gray-50 text-gray-500 border-gray-200"}>
                {finding.severityLabel}
              </Chip>
              <span className="text-gray-700">{finding.field ? finding.fieldLabel : PROVIDER_ONBOARDING_UNKNOWN_HE}</span>
              <span className="text-gray-500">{finding.reasonLabel}</span>
              {/* The server's own tokens stay on screen: they are what the API and the logs call this. */}
              <span className="font-mono text-gray-400">
                {finding.field || PROVIDER_ONBOARDING_UNKNOWN_HE} · {finding.reason || PROVIDER_ONBOARDING_UNKNOWN_HE}
              </span>
            </div>
          ))}
        </div>
      )}

      {fieldsToFix.length > 0 && (
        <div className="text-xs text-gray-500" data-testid="provider-onboarding-fields-to-fix">
          שדות לתיקון:{" "}
          {fieldsToFix.map((field) => (
            <span key={field} className="inline-block mr-2 font-mono text-gray-600">
              {field}
            </span>
          ))}
        </div>
      )}

      {/* The fields the server CHOSE to echo back. A field carrying a refusal or an incompleteness is
          dropped from the projection entirely, so an absent field here is not "the server forgot it" —
          it is the server declining to carry a value it would not vouch for. */}
      <div className="border rounded-lg p-3 space-y-1" data-testid="provider-onboarding-draft">
        <div className="text-xs font-semibold text-gray-500">הטיוטה כפי שהשרת קרא אותה</div>
        {Object.keys(draft).length === 0 && <Row label="—" value="אין שדה שאושר להצגה" />}
        {Object.entries(draft).map(([field, value]) => (
          <Row
            key={field}
            label={providerOnboardingFieldLabel(field)}
            value={Array.isArray(value) ? value.join(" · ") : String(value)}
            mono
          />
        ))}
      </div>

      <div className="border rounded-lg p-3 space-y-1" data-testid="provider-onboarding-credential">
        <div className="text-xs font-semibold text-gray-500">הפניית הסוד</div>
        <Row
          label="מצב"
          value={providerOnboardingCredentialStateLabel(credential.state)}
        />
        <Row label="סכימה" value={credential.scheme || "—"} mono />
        <Row
          label="אורך שם ההפניה"
          value={credential.reference_name_length == null ? "—" : String(credential.reference_name_length)}
        />
        {/* The digest is of the reference NAME, never of a value: the name itself is not echoed, so two
            references of the same length stay distinguishable in an audit trail. */}
        <Row label="טביעת שם ההפניה" value={credential.reference_name_digest || "—"} mono />
        <Row
          label="הערך הוחזר"
          value={providerOnboardingFalseFlag(credential.value_returned, "לא — הערך אינו מוחזר לעולם")}
        />
      </div>

      <div className="border rounded-lg p-3 space-y-1" data-testid="provider-onboarding-registry">
        <div className="text-xs font-semibold text-gray-500">הרישום שהבדיקה השוותה מולו</div>
        <Row label="מצב" value={providerOnboardingRegistryStateLabel(registry.state)} />
        <Row
          label="ספקים מוכרים בגרסה הזו"
          value={Array.isArray(registry.known_provider_ids) ? registry.known_provider_ids.join(" · ") : "—"}
          mono
        />
        <Row
          label="ספקים שיש להם הגדרת מפתח"
          value={
            Array.isArray(registry.credential_setting_providers)
              ? registry.credential_setting_providers.join(" · ")
              : "—"
          }
          mono
        />
        {/* A constant False on the wire: the preview compares a declaration against this build's own
            provider set, and it verifies nothing about the provider itself. Printed as its own line so
            the card never reads as though a declaration had been checked. */}
        <Row
          label="ההצהרות אומתו"
          value={providerOnboardingFalseFlag(registry.declarations_verified, "לא — ההצהרות אינן נבדקות כאן")}
        />
      </div>

      {/* Printed in BOTH states, on every answer, from the server's own block. */}
      <div className="border rounded-lg p-3 space-y-1" data-testid="provider-onboarding-activation">
        <div className="text-xs font-semibold text-gray-500">
          מה ההכנה הזו לא עשתה
        </div>
        <Row label="מצב ההפעלה" value={providerOnboardingActivationStateLabel(answer.activation?.state)} />
        <ul className="list-disc list-inside space-y-0.5 text-xs text-gray-600">
          {providerOnboardingActivationSentences(answer.activation).map((sentence) => (
            <li key={sentence}>{sentence}</li>
          ))}
        </ul>
      </div>

      <div className="border rounded-lg p-3 space-y-2" data-testid="provider-onboarding-boundaries">
        <div className="text-xs font-semibold text-gray-500">גבולות</div>
        <div className="flex flex-wrap gap-2">
          {providerOnboardingBoundaryLabels(answer.boundaries).map((label, index) => (
            <Chip key={answer.boundaries[index]} tone="bg-gray-50 text-gray-600 border-gray-200">
              {label}
            </Chip>
          ))}
        </div>
      </div>

      {/* Verbatim from the wire, and labelled as such: these are the preview's own sentences about what
          it cannot see, and a client-side copy of a sentence is a claim the server can invalidate. */}
      {Array.isArray(answer.caveats) && answer.caveats.length > 0 && (
        <div className="border rounded-lg p-3 space-y-1" data-testid="provider-onboarding-caveats">
          <div className="text-xs font-semibold text-gray-500">{PROVIDER_ONBOARDING_CAVEATS_NOTE_HE}</div>
          <ul dir="ltr" className="list-disc list-inside space-y-0.5 text-xs text-gray-600">
            {answer.caveats.map((caveat) => (
              <li key={caveat}>{caveat}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="text-xs text-gray-400 font-mono break-all" data-testid="provider-onboarding-digest">
        {answer.preview_digest}
      </div>
    </div>
  );
}

/**
 * The panel.
 *
 * Self-gated on the same `admin`/`owner` roles the two routes require: a technician who reached this
 * page would otherwise get two 403s and a broken form, and a surface that offers a control the actor
 * cannot use is worse than one that is absent. The route gate is the enforcement; this is the display.
 *
 * The contract is fetched by the panel and only when it is mounted. A contract that fails to load stops
 * the surface rather than falling back to a local option list — a form built from a guess is exactly the
 * failure the contract endpoint exists to prevent.
 */
export default function ProviderOnboardingPanel() {
  const { user } = useAuth() || {};
  const role = user?.role;
  const isAdmin = role === "admin" || role === "owner";

  const [fields, setFields] = useState(null);
  const [capabilities, setCapabilities] = useState([]);
  const [answer, setAnswer] = useState(null);

  const contractQuery = useQuery({
    queryKey: ["provider-onboarding-contract"],
    queryFn: connectionsApi.providerOnboardingContract,
    staleTime: Infinity,
    enabled: isAdmin,
  });

  const contract = contractQuery.data;

  useEffect(() => {
    if (contract && fields === null) {
      setFields(draftFromTemplate(contract));
      setCapabilities(capabilitiesFromTemplate(contract));
    }
  }, [contract, fields]);

  const previewMutation = useMutation({
    mutationFn: (payload) => connectionsApi.previewProviderOnboarding(payload),
    onSuccess: (data) => setAnswer(data),
  });

  if (!isAdmin) return null;

  function change(field, value) {
    setFields((current) => ({ ...current, [field]: value }));
  }

  function toggleCapability(token) {
    setCapabilities((current) =>
      current.includes(token) ? current.filter((entry) => entry !== token) : [...current, token]
    );
  }

  function submit() {
    setAnswer(null);
    previewMutation.mutate(payloadFrom(fields || {}, capabilities, contract));
  }

  return (
    <div
      className="md:col-span-2 border-2 border-brand-200 rounded-xl p-4 space-y-4 bg-brand-50/30"
      dir="rtl"
      data-testid="provider-onboarding-panel"
    >
      <div>
        <h2 className="font-semibold text-gray-800 text-sm">הכנת ספק חדש (הכנה ובדיקה בלבד)</h2>
        <p className="text-xs text-gray-500 mt-0.5">
          הטופס מכין טיוטה ומריץ עליה את בדיקת הקבילות של השרת. ההכנה אינה מקצה סוד, אינה בודקת חיבור,
          אינה מפעילה ספק ואינה משנה את הניתוב.
        </p>
      </div>

      {contractQuery.isLoading && <p className="text-sm text-gray-400">טוען את החוזה…</p>}
      {contractQuery.isError && (
        <p role="alert" className="text-sm text-red-600" data-testid="provider-onboarding-contract-error">
          לא ניתן לטעון את חוזה ההכנה — אין טופס שנבנה מניחוש.
        </p>
      )}

      {contract && fields !== null && (
        <>
          <ProviderOnboardingForm
            contract={contract}
            fields={fields}
            capabilities={capabilities}
            onChange={change}
            onToggle={toggleCapability}
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={submit}
              disabled={previewMutation.isPending}
              className="bg-brand-600 hover:bg-brand-700 text-white text-sm px-4 py-2 rounded-lg font-medium disabled:opacity-50"
              data-testid="provider-onboarding-submit"
            >
              {previewMutation.isPending ? "בודק…" : "בדוק את הטיוטה"}
            </button>
            <span className="text-xs text-gray-400">
              הבדיקה אינה שומרת דבר ואינה מפעילה דבר.
            </span>
          </div>
        </>
      )}

      {previewMutation.isError && (
        <p role="alert" className="text-sm text-red-600" data-testid="provider-onboarding-error">
          {previewMutation.error?.response?.data?.detail || "שגיאה בהרצת הבדיקה."}
        </p>
      )}

      <ProviderOnboardingAnswer answer={answer} contract={contract} />
    </div>
  );
}
